/**
 * Pure parts of the pobočka form's public fields (P7-b): the opening hours as seven weekday rows and the URL key (slug).
 * The server owns the rules and answers with its own message; these helpers only shape the form and give early feedback.
 */

import type { OtviraciDoba } from './api';

export const DNY = ['Pondělí', 'Úterý', 'Středa', 'Čtvrtek', 'Pátek', 'Sobota', 'Neděle'] as const;

/** One weekday of the form: closed, or open between two `HH:mm` times. */
export interface DenRadek {
  otevreno: boolean;
  od: string;
  do: string;
}

/** What a day opened with the checkbox starts with — the legacy site's pickup / return window. */
export const VYCHOZI_OD = '08:00';
export const VYCHOZI_DO = '15:00';

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
export const HHMM_PATTERN = '([01][0-9]|2[0-3]):[0-5][0-9]';

/** Seven rows, Monday first; a day with no stored interval is closed. */
export function hodinySeed(list: readonly OtviraciDoba[] = []): DenRadek[] {
  return DNY.map((_, i) => {
    const x = list.find((e) => e.den === i + 1);
    return x ? { otevreno: true, od: x.od, do: x.do } : { otevreno: false, od: VYCHOZI_OD, do: VYCHOZI_DO };
  });
}

/** Monday to Friday 08:00–15:00 (the legacy rule), the weekend closed. */
export const hodinyPracovniDny = (): DenRadek[] =>
  DNY.map((_, i) => ({ otevreno: i < 5, od: VYCHOZI_OD, do: VYCHOZI_DO }));

/** The open days only, with the weekday number the API uses. */
export function hodinyToBody(rows: readonly DenRadek[]): OtviraciDoba[] {
  return rows.flatMap((r, i) => (r.otevreno ? [{ den: i + 1, od: r.od.trim(), do: r.do.trim() }] : []));
}

/** Why the open days cannot be saved (Czech), or null. Mirrors the server's check for early feedback. */
export function hodinyChyba(rows: readonly DenRadek[]): string | null {
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    if (!r.otevreno) continue;
    if (!HHMM.test(r.od.trim()) || !HHMM.test(r.do.trim())) return `${DNY[i]}: čas zadejte ve tvaru HH:mm.`;
    if (r.od.trim() >= r.do.trim()) return `${DNY[i]}: otevření musí být před zavřením.`;
  }
  return null;
}

/** A short summary for the list: `po–pá 08:00–15:00`, or the days one by one when they differ. */
export function hodinyShrnuti(list: readonly OtviraciDoba[]): string {
  if (list.length === 0) return '';
  const zkr = ['po', 'út', 'st', 'čt', 'pá', 'so', 'ne'];
  const sorted = [...list].sort((a, b) => a.den - b.den);
  const sameWindow = sorted.every((x) => x.od === sorted[0].od && x.do === sorted[0].do);
  const consecutive = sorted.every((x, i) => i === 0 || x.den === sorted[i - 1].den + 1);
  if (sameWindow && consecutive) {
    const first = zkr[sorted[0].den - 1];
    const last = zkr[sorted[sorted.length - 1].den - 1];
    return `${sorted.length === 1 ? first : `${first}–${last}`} ${sorted[0].od}–${sorted[0].do}`;
  }
  return sorted.map((x) => `${zkr[x.den - 1]} ${x.od}–${x.do}`).join(', ');
}

/** The URL key the server accepts: lower-case letters, digits, single hyphens, at most 60 characters. */
export const SLUG_PATTERN = '\\s*[a-z0-9]+(-[a-z0-9]+)*\\s*|';
export const SLUG_MAX = 60;

/** True when a stored URL key would be changed or cleared — the old public links stop working, so the edit asks first. */
export const slugSeMeni = (puvodni: string | null | undefined, novy: string): boolean => !!puvodni && novy.trim() !== puvodni;

/** A suggestion from a name: "Žďár nad Sázavou" → "zdar-nad-sazavou". */
export function slugZNazvu(nazev: string): string {
  return nazev
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, SLUG_MAX)
    .replace(/-+$/g, '');
}
